"use client";

import { Flex, Text } from "@radix-ui/themes";
import { OrganizationDomain } from "../api/endpoint.js";
import { IconPanel } from "./icon-panel.js";
import { getComparativeReadableDate } from "./utils.js";
import { Status } from "./status.js";
import { NameServerIcon } from "./name-server-icon.js";
import { DomainActions } from "./domain-actions.js";
import { Translation } from "./i18n/translation.js";
import { useLocale } from "./i18n/use-locale.js";

interface DomainItemProps {
  domain: OrganizationDomain;
  onDeleteDomain: (domainId: string) => void;
  onReverifyDomain: (domainId: string) => void;
}

export const DomainItem = ({
  domain,
  onDeleteDomain,
  onReverifyDomain,
}: DomainItemProps) => {
  const locale = useLocale();

  return (
    <Flex gap="4" align="center">
      <IconPanel color={domain.nameServer === "Other" ? "gray" : "panel"}>
        <NameServerIcon provider={domain.nameServer} />
      </IconPanel>

      <Flex direction="column">
        <Text size="2" highContrast weight="bold" as="p">
          {domain.domain}
        </Text>

        <Text size="2" color="gray" as="p">
          <Translation
            defaultMessage="Added {timeAgo}"
            id="DtZ2fb"
            description="Label showing when the domain was added"
            values={{
              timeAgo: getComparativeReadableDate(
                new Date(),
                new Date(domain.createdAt),
                { locale },
              ),
            }}
          />
        </Text>
      </Flex>

      <Flex ml="auto" mr="2" gap="4" align="center">
        <Flex gap="1" align="center">
          <Status state={getDomainStatus(domain)}>
            {domainStateToLabel(domain)}
          </Status>
        </Flex>

        <DomainActions
          domain={domain}
          onDeleteDomain={onDeleteDomain}
          onReverifyDomain={onReverifyDomain}
        />
      </Flex>
    </Flex>
  );
};

const domainStateToLabel = (domain: OrganizationDomain) => {
  switch (domain.state) {
    case "Verified":
    case "LegacyVerified":
      return (
        <Translation
          defaultMessage="Verified"
          id="T7rpHw"
          description="Domain verification status indicating the domain is verified"
        />
      );
    case "Pending":
      return (
        <Translation
          defaultMessage="Verification pending"
          id="gc1yge"
          description="Domain verification status indicating verification is in progress"
        />
      );
    case "Failed":
      return (
        <Translation
          defaultMessage="Verification failed"
          id="cbbwqK"
          description="Domain verification status indicating verification has failed"
        />
      );
  }
};

const getDomainStatus = (domain: OrganizationDomain) => {
  switch (domain.state) {
    case "Verified":
    case "LegacyVerified":
      return "success";
    case "Pending":
      return "waiting";
    case "Failed":
      return "error";
  }
};
